import { useEffect, useState } from 'react';
import { Alert, Button, ButtonGroup, Form } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../components/common/Loader';
import PrescriptionSheet from '../components/print/PrescriptionSheet';
import { consultationApi } from '../api';

export default function PrintPrescription() {
  const { visitId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paper, setPaper] = useState('A4');

  useEffect(() => {
    consultationApi
      .printData(visitId)
      .then((res) => {
        setData(res.data);
        setPaper(res.data.clinic.paperSize || 'A4');
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [visitId]);

  // The browser's own @page rule decides the sheet size, so it is injected here
  // rather than living in the stylesheet.
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `@media print { @page { size: ${paper}; margin: 0; } }`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [paper]);

  if (loading) return <Loader label="Preparing the prescription..." className="py-5" />;

  if (error) {
    return (
      <Alert variant="warning" className="text-center">
        <i className="bi bi-exclamation-triangle fs-3 d-block mb-2" />
        {error}
        <div className="mt-3">
          <Button variant="light" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </div>
      </Alert>
    );
  }

  const sheetData = { ...data, clinic: { ...data.clinic, paperSize: paper } };

  return (
    <>
      <div className="no-print d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button variant="light" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left me-1" /> Back
          </Button>
          <div>
            <div className="fw-bold">{data.patient.name}</div>
            <div className="text-muted small">
              {data.patient.patientId} &middot; Token {data.visit.tokenNo}
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Form.Select value={paper} onChange={(e) => setPaper(e.target.value)} style={{ width: 110 }}>
            <option value="A4">A4</option>
            <option value="A5">A5</option>
          </Form.Select>

          <ButtonGroup>
            <Button variant="outline-secondary" onClick={() => navigate(`/patients/${data.patient._id}`)}>
              <i className="bi bi-person-lines-fill me-1" /> Record
            </Button>
            <Button className="btn-clinic" onClick={() => window.print()}>
              <i className="bi bi-printer me-1" /> Print
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <div className="no-print alert alert-light border small text-muted">
        <i className="bi bi-info-circle me-1" />
        In the print dialog set Margins to <strong>None</strong> and turn off "Headers and footers" so the sheet
        matches this preview exactly.
      </div>

      <div style={{ overflowX: 'auto', paddingBottom: '2rem' }}>
        <PrescriptionSheet data={sheetData} />
      </div>
    </>
  );
}
